/**
 * Time-formatting helpers for the freshness lag visualizations (bars + the
 * detail-modal line chart). Pure, dependency-free, UTC-only — extracted from
 * `LagHistoryBar.tsx` so the bar tooltip and the chart axis/tooltip share one
 * formatter and never drift.
 *
 * All output is UTC by design: the status page states facts in UTC (DESIGN.md
 * §11), and probe `runAt` timestamps are UTC ISO-8601.
 */

export type LagWindow = '90d' | '24h' | '1h';

/** `HH:MM` (24-hour, UTC) for a Date — the page's canonical clock format. */
export function formatHm(d: Date): string {
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** `Mon D, YYYY` (UTC) — used for 90d-window day labels. */
function formatFullDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Human label for a single bucket's time span, matching the existing bar
 * tooltip:
 *   - `90d` → `May 9, 2026`            (the day)
 *   - `24h` → `12:47 — 13:02 UTC`      (15-min span)
 *   - `1h`  → `12:47 UTC`              (the minute)
 * Empty `startAt` (placeholder/no-data cell) → `—`.
 */
export function formatLagTimeRange(
  bucket: { startAt: string; endAt: string },
  windowSel: LagWindow,
): string {
  if (!bucket.startAt) return '—';
  const start = new Date(bucket.startAt);
  if (windowSel === '90d') return formatFullDate(start);
  if (windowSel === '24h') {
    const end = new Date(bucket.endAt);
    return `${formatHm(start)} — ${formatHm(end)} UTC`;
  }
  return `${formatHm(start)} UTC`;
}

/**
 * Compact label for a single point on the chart's x-axis. Drops the `UTC`
 * suffix (the axis is space-constrained; the modal subtitle carries the
 * timezone context):
 *   - `90d` → `May 9`
 *   - `24h` / `1h` → `12:47`
 * Non-finite input → empty string (caller omits the tick).
 */
export function formatAxisTime(ms: number, windowSel: LagWindow): string {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  if (windowSel === '90d') {
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }
  return formatHm(d);
}
